import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Users, UserPlus, Trash2, Pencil, Shield, User, 
  Loader2, Check, X, Mail, Lock, Eye, EyeOff 
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/components/ui/sonner';

const RoleBadge = ({ role }) => {
  if (role === 'admin') {
    return (
      <Badge className="bg-indigo-500/10 text-indigo-400 border-indigo-500/30">
        <Shield className="w-3 h-3 mr-1" /> Admin
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-zinc-700 text-zinc-400">
      <User className="w-3 h-3 mr-1" /> Client
    </Badge>
  );
};

export default function UserManagement() {
  const { user: currentUser, listUsers, registerUser, updateUser, deleteUser, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'client' });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const data = await listUsers();
      setUsers(data);
    } catch (e) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [listUsers]);

  useEffect(() => {
    if (isAdmin) fetchUsers();
  }, [isAdmin, fetchUsers]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await registerUser(formData.email, formData.password, formData.name, formData.role);
      toast.success('User created successfully');
      setShowAddForm(false);
      setFormData({ name: '', email: '', password: '', role: 'client' });
      fetchUsers();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const updates = { name: formData.name, email: formData.email, role: formData.role };
      if (formData.password) updates.password = formData.password;
      await updateUser(editingUser.id, updates);
      toast.success('User updated successfully');
      setEditingUser(null);
      setFormData({ name: '', email: '', password: '', role: 'client' });
      fetchUsers();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await deleteUser(userId);
      toast.success('User deleted');
      fetchUsers();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const startEdit = (user) => {
    setEditingUser(user);
    setFormData({ name: user.name, email: user.email, password: '', role: user.role });
    setShowAddForm(false);
  };

  const cancelForm = () => {
    setShowAddForm(false);
    setEditingUser(null);
    setFormData({ name: '', email: '', password: '', role: 'client' });
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[40vh] text-zinc-500 text-sm">
        Admin access required
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="user-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-zinc-100 font-['Manrope']">User Management</h2>
          <p className="text-sm text-zinc-400 mt-1">Manage admin and client access</p>
        </div>
        {!showAddForm && !editingUser && (
          <Button
            onClick={() => setShowAddForm(true)}
            className="bg-indigo-600 hover:bg-indigo-500 text-white"
            data-testid="add-user-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" /> Add User
          </Button>
        )}
      </div>

      {/* Add/Edit Form */}
      {(showAddForm || editingUser) && (
        <Card className="bg-zinc-900/60 border-indigo-500/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-zinc-200">
              {editingUser ? 'Edit User' : 'Add New User'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={editingUser ? handleUpdateUser : handleAddUser} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Smith"
                      required
                      className="w-full bg-zinc-800/60 border border-zinc-700 rounded-lg text-sm text-zinc-200 py-2 pl-10 pr-4 focus:border-indigo-500/50 focus:outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@company.com"
                      required
                      className="w-full bg-zinc-800/60 border border-zinc-700 rounded-lg text-sm text-zinc-200 py-2 pl-10 pr-4 focus:border-indigo-500/50 focus:outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">
                    Password {editingUser && <span className="text-zinc-600">(leave blank to keep current)</span>}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="••••••••"
                      required={!editingUser}
                      minLength={editingUser ? 0 : 6}
                      className="w-full bg-zinc-800/60 border border-zinc-700 rounded-lg text-sm text-zinc-200 py-2 pl-10 pr-10 focus:border-indigo-500/50 focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">Role</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full bg-zinc-800/60 border border-zinc-700 rounded-lg text-sm text-zinc-200 py-2 px-3 focus:border-indigo-500/50 focus:outline-none"
                  >
                    <option value="client">Client (Read-only)</option>
                    <option value="admin">Admin (Full access)</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-indigo-600 hover:bg-indigo-500 text-white"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                  {editingUser ? 'Update User' : 'Create User'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={cancelForm}
                  className="border-zinc-700 text-zinc-400"
                >
                  <X className="w-4 h-4 mr-2" /> Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Users List */}
      <Card className="bg-zinc-900/60 border-zinc-800/60">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <Users className="w-4 h-4 text-indigo-400" />
              All Users
            </CardTitle>
            <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-xs">
              {users.length} user{users.length !== 1 ? 's' : ''}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-indigo-400 animate-spin" />
            </div>
          ) : users.length === 0 ? (
            <p className="text-center text-zinc-500 py-8">No users found</p>
          ) : (
            <div className="space-y-2">
              {users.map((user) => (
                <div
                  key={user.id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    user.id === currentUser?.id
                      ? 'border-indigo-500/30 bg-indigo-500/5'
                      : 'border-zinc-800/50 bg-zinc-800/20'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                      user.role === 'admin' ? 'bg-indigo-500/20' : 'bg-zinc-700/50'
                    }`}>
                      {user.role === 'admin' ? (
                        <Shield className="w-4 h-4 text-indigo-400" />
                      ) : (
                        <User className="w-4 h-4 text-zinc-400" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-zinc-200">{user.name}</span>
                        {user.id === currentUser?.id && (
                          <Badge variant="outline" className="border-emerald-500/30 text-emerald-400 text-[9px]">You</Badge>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500">{user.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <RoleBadge role={user.role} />
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => startEdit(user)}
                        className="h-8 w-8 p-0 text-zinc-500 hover:text-zinc-300"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      {user.id !== currentUser?.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteUser(user.id)}
                          className="h-8 w-8 p-0 text-zinc-500 hover:text-red-400"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Role Explanation */}
      <Card className="bg-zinc-900/60 border-zinc-800/60">
        <CardContent className="p-4">
          <h4 className="text-xs font-medium text-zinc-300 mb-3">Role Permissions</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
            <div className="p-3 rounded-lg bg-indigo-500/5 border border-indigo-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-indigo-400" />
                <span className="font-medium text-indigo-300">Admin</span>
              </div>
              <ul className="space-y-1 text-zinc-400">
                <li>• Create, edit, delete analyses</li>
                <li>• Manage users</li>
                <li>• Generate reports</li>
                <li>• Full access to all features</li>
              </ul>
            </div>
            <div className="p-3 rounded-lg bg-zinc-800/30 border border-zinc-700/50">
              <div className="flex items-center gap-2 mb-2">
                <User className="w-4 h-4 text-zinc-400" />
                <span className="font-medium text-zinc-300">Client</span>
              </div>
              <ul className="space-y-1 text-zinc-400">
                <li>• View shared analyses</li>
                <li>• Download reports</li>
                <li>• Read-only access</li>
                <li>• Cannot modify data</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
